import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Users, Shield, ArrowRight, CheckCircle, BarChart, Search, Layers, User, CheckSquare, ShieldCheck, Lock, ScrollText, Brain, Hourglass, Key, Cpu, Rocket, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import SecurityCardsCarousel from '../components/SecurityCardsCarousel';
import OrdinaAIFlow from '../components/OrdinaAIFlow';

// Add CSS for ambient drift animation and stack card animations
const ambientDriftStyles = `
  @keyframes ambientDrift {
    0%, 100% {
      background-position: 0% 0%, 100% 100%, 30% 70%, 0% 0%;
    }
    25% {
      background-position: 5% 10%, 95% 85%, 25% 65%, 0% 2%;
    }
    50% {
      background-position: 10% 5%, 90% 95%, 35% 75%, 0% -2%;
    }
    75% {
      background-position: 3% 8%, 97% 90%, 28% 68%, 0% 1%;
    }
  }

  .hero-ambient-bg {
    animation: ambientDrift 75s ease-in-out infinite;
    background-size: 200% 200%, 200% 200%, 200% 200%, 100% 100% !important;
  }

  /* Stack card ambient glow animations */
  @keyframes glowPulse {
    0%, 100% {
      box-shadow:
        0 0 20px rgba(217, 119, 6, 0.3),
        0 0 40px rgba(217, 119, 6, 0.15),
        inset 0 0 20px rgba(217, 119, 6, 0.05);
    }
    50% {
      box-shadow:
        0 0 30px rgba(217, 119, 6, 0.5),
        0 0 60px rgba(217, 119, 6, 0.25),
        inset 0 0 30px rgba(217, 119, 6, 0.1);
    }
  }

  @keyframes timeDrain {
    0% { transform: rotate(0deg); opacity: 1; }
    100% { transform: rotate(360deg); opacity: 1; }
  }

  @keyframes lockShimmer {
    0%, 100% { opacity: 0; }
    50% { opacity: 0.4; }
  }

  @keyframes morphLines {
    0%, 100% { stroke-dashoffset: 0; }
    50% { stroke-dashoffset: 10px; }
  }

  @keyframes shieldDraw {
    0% { stroke-dashoffset: 200; }
    100% { stroke-dashoffset: 0; }
  }

  @keyframes subtleLift {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }

  /* Darker purple glow pulse animation for all cards - only on hover */
  @keyframes glowPulsePurple {
    0%, 100% {
      box-shadow:
        0 0 30px rgba(109, 40, 217, 0.25),
        0 0 60px rgba(109, 40, 217, 0.12),
        inset 0 0 20px rgba(109, 40, 217, 0.05);
      border-color: rgba(109, 40, 217, 0.4);
    }
    50% {
      box-shadow:
        0 0 40px rgba(109, 40, 217, 0.4),
        0 0 80px rgba(109, 40, 217, 0.2),
        inset 0 0 30px rgba(109, 40, 217, 0.1);
      border-color: rgba(109, 40, 217, 0.6);
    }
  }

  /* Ambient gradient background animations - slow */
  @keyframes ambientGradientSlow {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }

  /* Ambient gradient background animations - fast (on hover) */
  @keyframes ambientGradientFast {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }

  .stack-card {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .stack-card:hover {
    transform: scale(1.4);
  }

  /* Premium dark theme backgrounds with white text */
  .stack-card.glow-amber {
    background: linear-gradient(135deg, #1f1f2e 0%, #2a2a3e 100%);
    color: white;
  }

  .stack-card.glow-orange {
    background: linear-gradient(135deg, #1f1f2e 0%, #2a2a3e 100%);
    color: white;
  }

  .stack-card.glow-red {
    background: linear-gradient(135deg, #1f1f2e 0%, #2a2a3e 100%);
    color: white;
  }

  /* Transition to purple on reveal */
  .stack-card.glow-amber[style*="opacity: 0"],
  .stack-card.glow-orange[style*="opacity: 0"],
  .stack-card.glow-red[style*="opacity: 0"] {
    background: linear-gradient(135deg, #2d1b4e 0%, #3d2d5e 100%);
  }

  .stack-card.glow-purple {
    background: linear-gradient(135deg, #2d1b4e 0%, #3d2d5e 100%);
    color: white;
  }

  /* Dark purple glow animation for all cards - only on hover */
  .stack-card.glow-amber:hover,
  .stack-card.glow-orange:hover,
  .stack-card.glow-red:hover,
  .stack-card.glow-purple:hover {
    animation: glowPulsePurple 1.5s ease-in-out infinite;
    box-shadow:
      0 0 30px rgba(109, 40, 217, 0.25),
      0 0 60px rgba(109, 40, 217, 0.12),
      inset 0 0 20px rgba(109, 40, 217, 0.05);
    border-color: rgba(109, 40, 217, 0.4);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = ambientDriftStyles;
  document.head.appendChild(styleElement);
}

const Home = () => {
  const [revealedCards, setRevealedCards] = React.useState({});
  const [hoveredProductCard, setHoveredProductCard] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(false);

  // Mobile detection for animation optimization
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const toggleReveal = (cardId) => {
    setRevealedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="hero-ambient-bg relative py-20 lg:py-32 overflow-hidden" style={{
        background: `
          radial-gradient(ellipse 600px 500px at 5% 10%, rgba(6, 182, 212, 0.35) 0%, rgba(6, 182, 212, 0.15) 30%, transparent 65%),
          radial-gradient(ellipse 500px 450px at 20% 35%, rgba(6, 182, 212, 0.28) 0%, rgba(6, 182, 212, 0.12) 35%, transparent 65%),
          radial-gradient(ellipse 650px 550px at 95% 85%, rgba(168, 85, 247, 0.4) 0%, rgba(168, 85, 247, 0.15) 35%, transparent 70%),
          radial-gradient(ellipse 500px 400px at 15% 85%, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.08) 40%, transparent 60%),
          linear-gradient(to bottom, #fafbff, #ffffff)
        `,
        backgroundSize: 'auto, auto, auto, auto, 100% 100%',
      }}>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 tracking-tight animate-slide-up">
              Powering the <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-600">Future of Intelligence</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Ordinaa builds AI-native, quantum-safe platforms that don't just analyze data, they act on it across Finance, Recruitment, Tender and PQC workflows.
            </p>

            <div className="mb-16 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                AI that Automates High-Stakes Enterprise Decisions
              </h2>

              <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8 mb-12">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">Secure</div>
                </div>
                <div className="hidden md:block h-6 w-px bg-gradient-to-b from-transparent via-gray-400 to-transparent"></div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">Agentic</div>
                </div>
                <div className="hidden md:block h-6 w-px bg-gradient-to-b from-transparent via-gray-400 to-transparent"></div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">Quantum-Ready</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <button
                onClick={() => document.getElementById('suite-of-ai-applications')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all hover:scale-105 flex items-center gap-2 cursor-pointer border-none"
              >
                Explore Solutions <ArrowRight size={20} />
              </button>
              <Link
                to="/contact"
                className="px-8 py-4 bg-cyan-500 text-white rounded-full font-semibold hover:bg-cyan-600 transition-all hover:scale-105 flex items-center gap-2"
              >
                Talk to Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Designed for High-Trust, High-Impact Operations */}
      <section className="py-12 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
      }}>
        {/* Subtle animated background elements */}
        <motion.div
          className="absolute top-1/2 right-0 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)',
          }}
          animate={{
            y: [0, 30, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            Designed for High-Trust, High-Impact Operations
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* AI That Acts Card */}
            <motion.div
              className="group relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl md:rounded-3xl p-5 md:p-8 border border-slate-700 hover:border-cyan-500"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 0 40px rgba(6, 182, 212, 0.8), 0 0 80px rgba(6, 182, 212, 0.5)"
              }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl md:rounded-2xl flex items-center justify-center text-white mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <svg className="w-5 h-5 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <motion.h3
                className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 group-hover:text-cyan-300 transition-colors"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                AI That Acts
              </motion.h3>
              <ul className="space-y-3 md:space-y-4">
                {['Agentic workflows', 'Autonomous decisions', 'Zero manual intervention'].map((item, i) => (
                  <motion.li
                    key={i}
                    className="flex items-start gap-2 md:gap-3 text-sm md:text-base text-gray-300 group-hover:text-white transition-colors"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 + i * 0.1, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <span className="text-cyan-400 text-base md:text-lg mt-0.5">→</span>
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Quantum-Safe by Design Card */}
            <motion.div
              className="group relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl md:rounded-3xl p-5 md:p-8 border border-slate-700 hover:border-cyan-500"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 0 40px rgba(168, 85, 247, 0.8), 0 0 80px rgba(168, 85, 247, 0.5)"
              }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-xl md:rounded-2xl flex items-center justify-center text-white mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <svg className="w-5 h-5 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <motion.h3
                className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 group-hover:text-cyan-300 transition-colors"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.25, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                Quantum-Safe by Design
              </motion.h3>
              <ul className="space-y-3 md:space-y-4">
                {['Post-quantum cryptography', 'Compliance-ready', 'Future-proof security'].map((item, i) => (
                  <motion.li
                    key={i}
                    className="flex items-start gap-2 md:gap-3 text-sm md:text-base text-gray-300 group-hover:text-white transition-colors"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.35 + i * 0.1, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <span className="text-cyan-400 text-base md:text-lg mt-0.5">→</span>
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Zero-Friction Adoption Card */}
            <motion.div
              className="group relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl md:rounded-3xl p-5 md:p-8 border border-slate-700 hover:border-cyan-500"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 0 40px rgba(6, 182, 212, 0.8), 0 0 80px rgba(6, 182, 212, 0.5)"
              }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl md:rounded-2xl flex items-center justify-center text-white mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <svg className="w-5 h-5 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <motion.h3
                className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 group-hover:text-cyan-300 transition-colors"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.35, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                Zero-Friction Adoption
              </motion.h3>
              <ul className="space-y-3 md:space-y-4">
                {['Seamless legacy integration', 'No retraining required', 'Enterprise-grade UX'].map((item, i) => (
                  <motion.li
                    key={i}
                    className="flex items-start gap-2 md:gap-3 text-sm md:text-base text-gray-300 group-hover:text-white transition-colors"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.45 + i * 0.1, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <span className="text-cyan-400 text-base md:text-lg mt-0.5">→</span>
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Smooth fade transition to next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(6, 182, 212, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
          }}
        />
      </section>

      {/* From Legacy Processes to Smart Workflows */}
      <section className="py-12 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
      }}>
        {/* Animated shimmer effect */}
        <motion.div
          className="absolute -top-1/2 left-1/3 w-full h-full rounded-full blur-3xl opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, 50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            From Legacy Processes to Smart Workflows
          </motion.h2>
          <motion.div
            className="flex justify-center mb-16"
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></div>
          </motion.div>

          {/* AI Flow Diagram - Full Animations on Mobile and Desktop */}
          <div className="w-full max-w-6xl mx-auto mb-16">
            <OrdinaAIFlow />
          </div>

          {/* Description Points */}

          <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
            {/* Left Column */}
            <div className="space-y-12 md:space-y-32">
              {/* Left Point 1 */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0 }}
                viewport={{ once: true }}
                className="flex justify-start group overflow-hidden"
              >
                <div className="border-l-4 border-cyan-500 pl-4 py-2 w-full">
                  <h3 className="text-lg md:text-xl font-bold text-cyan-600 mb-2">
                    Enterprise-Ready Integration
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Seamlessly bridges legacy systems with next-gen AI. Connects with existing infrastructure without disruption.
                  </p>
                </div>
              </motion.div>

              {/* Left Point 2 */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="flex justify-start group overflow-hidden"
              >
                <div className="border-l-4 border-green-500 pl-4 py-2 w-full">
                  <h3 className="text-lg md:text-xl font-bold text-green-600 mb-2">
                    Agentic, Not Just Automated
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    AI that reasons and adapts in real-time. Intelligent orchestration for complex enterprise challenges.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Column */}
            <div className="flex items-center">
              {/* Right Point */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="flex justify-end group overflow-hidden w-full"
              >
                <div className="border-l-4 md:border-l-0 md:border-r-4 border-blue-500 pl-4 md:pl-0 md:pr-4 py-2 text-left md:text-right w-full">
                  <h3 className="text-lg md:text-xl font-bold text-blue-600 mb-2">
                    Built for Regulated Environments
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    100% auditable and transparent. Every decision traceable—enterprise trust is non-negotiable.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="suite-of-ai-applications" className="py-24 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(59, 130, 246, 0.06) 50%, rgba(168, 85, 247, 0.08) 100%)',
      }}>
        {/* Animated gradient orbs in background */}
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl opacity-40"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, 20, -10, 0],
            y: [0, -30, 15, 0],
            scale: [1, 1.1, 0.95, 1],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-32 -left-32 w-72 h-72 rounded-full blur-3xl opacity-40"
          style={{
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.25) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, -25, 15, 0],
            y: [0, 35, -20, 0],
            scale: [1, 1.15, 0.9, 1],
          }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <motion.div
          className="absolute -bottom-20 right-0 w-80 h-80 rounded-full blur-3xl opacity-40"
          style={{
            background: 'radial-gradient(circle, rgba(147, 51, 234, 0.3) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -25, 20, 0],
            scale: [1, 1.1, 0.95, 1],
          }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        {/* Shimmer lines */}
        <motion.div
          className="absolute top-0 left-1/4 w-px h-full"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(6, 182, 212, 0.3) 50%, transparent 100%)',
          }}
          animate={{
            opacity: [0.3, 0.7, 0.3],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          className="absolute top-0 right-1/4 w-px h-full"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(168, 85, 247, 0.3) 50%, transparent 100%)',
          }}
          animate={{
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 5, repeat: Infinity, delay: 0.5 }}
        />

        {/* Content wrapper */}
        <div className="max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              Our Suite of AI Applications
            </motion.h2>
            <motion.p
              className="text-lg text-gray-600 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              Specialized platforms designed to solve complex enterprise challenges with cutting-edge artificial intelligence.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* DeepFlow Card - Top Left */}
            <motion.div
              className="group relative bg-white border-2 border-red-300/40 rounded-2xl md:rounded-3xl p-5 md:p-8 h-full shadow-lg shadow-red-500/10 cursor-pointer"
              style={{
                transform: hoveredProductCard === 'deepflow' ? 'scale(1.05)' : 'scale(1)',
                boxShadow: hoveredProductCard === 'deepflow' ? '0 20px 50px rgba(239, 68, 68, 0.3)' : 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease-in-out'
              }}
              onMouseEnter={() => setHoveredProductCard('deepflow')}
              onMouseLeave={() => setHoveredProductCard(null)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl md:rounded-2xl flex items-center justify-center text-white mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-red-500/30">
                <Layers size={24} className="md:hidden group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
                <Layers size={32} className="hidden md:block group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-red-600 transition-colors">DeepFlow AI</h3>
              <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                Advanced process mining and workflow optimization platform. Uncover hidden bottlenecks, automate repetitive tasks, and streamline operations for maximum efficiency.
              </p>
              <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Process Mining
                </li>
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Workflow Optimization
                </li>
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Bottleneck Detection
                </li>
              </ul>
              <Link to="/products/deepflow" className="inline-flex items-center text-sm md:text-base text-red-600 font-semibold hover:gap-2 transition-all">
                Learn more <ArrowRight size={14} className="md:hidden ml-1" /><ArrowRight size={16} className="hidden md:block ml-1" />
              </Link>
            </motion.div>

            {/* Niyukti Card - Top Center */}
            <motion.div
              className="group relative bg-white border-2 border-cyan-300/40 rounded-2xl md:rounded-3xl p-5 md:p-8 h-full shadow-lg shadow-cyan-500/10 cursor-pointer"
              style={{
                transform: hoveredProductCard === 'niyukti' ? 'scale(1.05)' : 'scale(1)',
                boxShadow: hoveredProductCard === 'niyukti' ? '0 20px 50px rgba(6, 182, 212, 0.3)' : 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease-in-out'
              }}
              onMouseEnter={() => setHoveredProductCard('niyukti')}
              onMouseLeave={() => setHoveredProductCard(null)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl md:rounded-2xl flex items-center justify-center text-white mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/30">
                <Users size={24} className="md:hidden group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
                <Users size={32} className="hidden md:block group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-cyan-600 transition-colors">Niyukti AI</h3>
              <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                An AI-powered application filtering and workflow tool designed to revolutionize recruitment. Streamline resume screening and automate candidate journeys with intelligent precision.
              </p>
              <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Intelligent Filtering
                </li>
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Workflow Automation
                </li>
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Talent Acquisition
                </li>
              </ul>
              <Link to="/products/niyukti" className="inline-flex items-center text-sm md:text-base text-cyan-600 font-semibold hover:gap-2 transition-all">
                Learn more <ArrowRight size={14} className="md:hidden ml-1" /><ArrowRight size={16} className="hidden md:block ml-1" />
              </Link>
            </motion.div>

            {/* LendSmart Card - Top Right */}
            <motion.div
              className="group relative bg-white border-2 border-blue-300/40 rounded-2xl md:rounded-3xl p-5 md:p-8 h-full shadow-lg shadow-blue-500/10 cursor-pointer"
              style={{
                transform: hoveredProductCard === 'lendsmart' ? 'scale(1.05)' : 'scale(1)',
                boxShadow: hoveredProductCard === 'lendsmart' ? '0 20px 50px rgba(59, 130, 246, 0.3)' : 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease-in-out'
              }}
              onMouseEnter={() => setHoveredProductCard('lendsmart')}
              onMouseLeave={() => setHoveredProductCard(null)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl md:rounded-2xl flex items-center justify-center text-white mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/30">
                <Activity size={24} className="md:hidden group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
                <Activity size={32} className="hidden md:block group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-blue-600 transition-colors">LendSmart AI</h3>
              <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                Automate complex workflows for commercial lending, loan origination, underwriting, and document processing,etc. Transform financial operations with AI-powered intelligence and reduce processing time by 85%.
              </p>
              <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> BFSI Workflow Automation
                </li>
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Document Intelligence
                </li>
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Risk Analysis
                </li>
              </ul>
              <Link to="/products/lendsmart" className="inline-flex items-center text-sm md:text-base text-blue-600 font-semibold hover:gap-2 transition-all">
                Learn more <ArrowRight size={14} className="md:hidden ml-1" /><ArrowRight size={16} className="hidden md:block ml-1" />
              </Link>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 place-items-center w-full md:w-auto" style={{maxWidth: '100%'}}>
            {/* TenderLens Card - Bottom Left */}
            <motion.div
              className="group relative bg-white border-2 border-orange-300/40 rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-lg shadow-orange-500/10 w-full"
              style={{
                minHeight: 'auto',
                transform: hoveredProductCard === 'tenderlens' ? 'scale(1.05)' : 'scale(1)',
                boxShadow: hoveredProductCard === 'tenderlens' ? '0 20px 50px rgba(251, 146, 60, 0.3)' : 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease-in-out'
              }}
              onMouseEnter={() => setHoveredProductCard('tenderlens')}
              onMouseLeave={() => setHoveredProductCard(null)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl md:rounded-2xl flex items-center justify-center text-white mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-amber-500/30">
                <Search size={24} className="md:hidden group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
                <Search size={32} className="hidden md:block group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-amber-600 transition-colors">TenderLens AI</h3>
              <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                Intelligent government tender discovery and analysis platform. Uncover opportunities with AI-powered insights, automated compliance checks, and intelligent bid recommendations.
              </p>
              <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Tender Discovery
                </li>
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Opportunity Analysis
                </li>
                <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                  <CheckCircle size={14} className="md:hidden text-cyan-500" />
                  <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Compliance Matching
                </li>
              </ul>
              <Link to="/products/tenderlens" className="inline-flex items-center text-sm md:text-base text-amber-600 font-semibold hover:gap-2 transition-all">
                Learn more <ArrowRight size={14} className="md:hidden ml-1" /><ArrowRight size={16} className="hidden md:block ml-1" />
              </Link>
            </motion.div>

            {/* Quantum Threat Awareness Stack - Bottom Right */}
            <motion.div
              className="relative w-full"
              style={{ minHeight: '320px', height: 'auto' }}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 2, delay: 0.5, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
            >
              {/* Card 1: Your Digital Security Has an Expiration Date */}
              <motion.div
                className="stack-card glow-amber absolute inset-0 bg-white border-2 border-amber-200/60 rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-lg shadow-amber-200/30 cursor-pointer overflow-hidden group"
                onClick={() => toggleReveal('quantum-card-1')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                animate={{
                  opacity: revealedCards['quantum-card-1'] ? 0 : 1,
                  zIndex: revealedCards['quantum-card-1'] ? 0 : 40,
                }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                style={{
                  pointerEvents: revealedCards['quantum-card-1'] ? 'none' : 'auto',
                }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ y: -6 }}
              >
                <div className="h-full flex flex-col items-center justify-center relative z-10">
                  <motion.div
                    className="w-12 h-12 md:w-16 md:h-16 bg-amber-100 rounded-xl md:rounded-2xl flex items-center justify-center text-amber-600 mb-4 md:mb-6"
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  >
                    <Hourglass size={24} className="md:hidden" strokeWidth={2} />
                    <Hourglass size={32} className="hidden md:block" strokeWidth={2} />
                  </motion.div>
                  <h3 className="text-lg md:text-2xl font-bold text-white mb-2 text-center">Your Digital Security Has an Expiration Date</h3>
                  <p className="text-gray-300 text-center mt-2 md:mt-3 text-xs">Discover what's coming →</p>
                  <p className="absolute bottom-3 md:bottom-4 text-gray-400 text-xs tracking-widest"><span className="hidden md:inline">click</span><span className="md:hidden">tap</span></p>
                </div>
              </motion.div>

              {/* Card 2: Your Locks Still Look Safe */}
              <motion.div
                className="stack-card glow-orange absolute inset-0 bg-white border-2 border-orange-200/60 rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-lg shadow-orange-200/30 cursor-pointer overflow-hidden group"
                onClick={() => toggleReveal('quantum-card-2')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                animate={{
                  opacity: revealedCards['quantum-card-1'] && !revealedCards['quantum-card-2'] ? 1 : 0,
                  zIndex: revealedCards['quantum-card-1'] && !revealedCards['quantum-card-2'] ? 30 : 0,
                }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                style={{
                  pointerEvents: revealedCards['quantum-card-1'] && !revealedCards['quantum-card-2'] ? 'auto' : 'none',
                }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ y: -6 }}
              >
                <div className="h-full flex flex-col items-center justify-center relative z-10">
                  <motion.div
                    className="w-12 h-12 md:w-16 md:h-16 bg-orange-100 rounded-xl md:rounded-2xl flex items-center justify-center text-orange-600 mb-4 md:mb-6 relative"
                    animate={{ y: [0, -2, 0] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Key size={24} className="md:hidden" strokeWidth={2} />
                    <Key size={32} className="hidden md:block" strokeWidth={2} />
                    <motion.div
                      className="absolute inset-0 bg-white rounded-xl md:rounded-2xl opacity-0"
                      animate={{ opacity: [0, 0.3, 0] }}
                      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                    />
                  </motion.div>
                  <h3 className="text-lg md:text-2xl font-bold text-white mb-2 text-center">Does Your Digital Lock Look Safe?</h3>
                  <p className="text-gray-300 text-center mt-2 md:mt-3 text-xs">Uncover the hidden threat →</p>
                  <p className="absolute bottom-3 md:bottom-4 text-gray-400 text-xs tracking-widest"><span className="hidden md:inline">click</span><span className="md:hidden">tap</span></p>
                </div>
              </motion.div>

              {/* Card 3: Quantum Changes the Rules */}
              <motion.div
                className="stack-card glow-red absolute inset-0 bg-white border-2 border-red-200/60 rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-lg shadow-red-200/30 cursor-pointer overflow-hidden group"
                onClick={() => toggleReveal('quantum-card-3')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                animate={{
                  opacity: revealedCards['quantum-card-2'] && !revealedCards['quantum-card-3'] ? 1 : 0,
                  zIndex: revealedCards['quantum-card-2'] && !revealedCards['quantum-card-3'] ? 30 : 0,
                }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                style={{
                  pointerEvents: revealedCards['quantum-card-2'] && !revealedCards['quantum-card-3'] ? 'auto' : 'none',
                }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ y: -6 }}
              >
                <div className="h-full flex flex-col items-center justify-center relative z-10">
                  <motion.div
                    className="w-12 h-12 md:w-16 md:h-16 bg-red-100 rounded-xl md:rounded-2xl flex items-center justify-center text-red-600 mb-4 md:mb-6"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Cpu size={24} className="md:hidden" strokeWidth={2} />
                    <Cpu size={32} className="hidden md:block" strokeWidth={2} />
                  </motion.div>
                  <h3 className="text-lg md:text-2xl font-bold text-white mb-2 text-center">Quantum Changes the Rules</h3>
                  <p className="text-sm md:text-base text-gray-200 text-center font-medium">(it isn't the future, it's here)</p>
                  <p className="text-gray-300 text-center mt-2 md:mt-3 text-xs">See what needs to shift →</p>
                  <p className="absolute bottom-3 md:bottom-4 text-gray-400 text-xs tracking-widest"><span className="hidden md:inline">click</span><span className="md:hidden">tap</span></p>
                </div>
              </motion.div>

              {/* Card 4: Start Before You're Forced */}
              <motion.div
                className="stack-card glow-purple absolute inset-0 bg-white border-2 border-pink-200/60 rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-lg shadow-pink-200/30 cursor-pointer overflow-hidden group"
                onClick={() => toggleReveal('quantum-card-4')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                animate={{
                  opacity: revealedCards['quantum-card-3'] && !revealedCards['quantum-card-4'] ? 1 : 0,
                  zIndex: revealedCards['quantum-card-3'] && !revealedCards['quantum-card-4'] ? 30 : 0,
                }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                style={{
                  pointerEvents: revealedCards['quantum-card-3'] && !revealedCards['quantum-card-4'] ? 'auto' : 'none',
                }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ y: -6 }}
              >
                <div className="h-full flex flex-col items-center justify-center relative z-10">
                  <motion.div
                    className="w-12 h-12 md:w-16 md:h-16 bg-pink-100 rounded-xl md:rounded-2xl flex items-center justify-center text-pink-600 mb-4 md:mb-6"
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Rocket size={24} className="md:hidden" strokeWidth={2} />
                    <Rocket size={32} className="hidden md:block" strokeWidth={2} />
                  </motion.div>
                  <h3 className="text-lg md:text-2xl font-bold text-white mb-2 text-center">Start Before You're Forced</h3>
                  <p className="text-sm md:text-base text-gray-200 text-center font-medium">(Act Before It's Mandated)</p>
                  <p className="text-gray-300 text-center mt-2 md:mt-3 text-xs">Find your path forward →</p>
                  <p className="absolute bottom-3 md:bottom-4 text-gray-400 text-xs tracking-widest"><span className="hidden md:inline">click</span><span className="md:hidden">tap</span></p>
                </div>
              </motion.div>

              {/* Card 5: PQC Migration - The Solution */}
              <motion.div
                className="group relative bg-white border-2 border-purple-300/60 rounded-2xl md:rounded-3xl p-5 md:p-8 shadow-lg shadow-purple-500/20"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                animate={{
                  opacity: revealedCards['quantum-card-4'] ? 1 : 0,
                  zIndex: revealedCards['quantum-card-4'] ? 20 : -10,
                  pointerEvents: revealedCards['quantum-card-4'] ? 'auto' : 'none',
                }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                style={{
                  pointerEvents: revealedCards['quantum-card-4'] ? 'auto' : 'none',
                  minHeight: 'auto',
                  transform: hoveredProductCard === 'pqc' ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: hoveredProductCard === 'pqc' ? '0 20px 50px rgba(147, 51, 234, 0.3)' : 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 10px 15px -3px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={() => setHoveredProductCard('pqc')}
                onMouseLeave={() => setHoveredProductCard(null)}
                viewport={{ once: true, margin: "-100px" }}
              >
                <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl md:rounded-2xl flex items-center justify-center text-white mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-purple-500/30">
                  <Shield size={24} className="md:hidden group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
                  <Shield size={32} className="hidden md:block group-hover:rotate-360 transition-transform duration-1000 ease-in-out" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-4 group-hover:text-purple-600 transition-colors">PQC Migration</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                  Seamlessly transition infrastructure and applications from quantum-unsafe cryptography (RSA,ECC) to post quantum algorithms like ML-KEM and ML-DSA.
                </p>
                <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                  <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                    <CheckCircle size={14} className="md:hidden text-cyan-500" />
                    <CheckCircle size={16} className="hidden md:block text-cyan-500" /> See What Needs to Change
                  </li>
                  <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                    <CheckCircle size={14} className="md:hidden text-cyan-500" />
                    <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Get Ready for What's Next
                  </li>
                  <li className="flex items-center gap-2 text-xs md:text-sm text-gray-600">
                    <CheckCircle size={14} className="md:hidden text-cyan-500" />
                    <CheckCircle size={16} className="hidden md:block text-cyan-500" /> Move at Your Own Pace
                  </li>
                </ul>
                <div className="flex justify-between items-center">
                  <Link to="/products/qsm" className="inline-flex items-center text-sm md:text-base text-purple-600 font-semibold hover:gap-2 transition-all">
                    Learn more <ArrowRight size={14} className="md:hidden ml-1" /><ArrowRight size={16} className="hidden md:block ml-1" />
                  </Link>
                  <button
                    onClick={() => {
                      setRevealedCards({
                        'quantum-card-1': false,
                        'quantum-card-2': false,
                        'quantum-card-3': false,
                        'quantum-card-4': false,
                      });
                    }}
                    className="inline-flex items-center text-xs md:text-sm text-gray-400 font-semibold hover:text-gray-500 transition-all"
                  >
                    Reset
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enterprise-Grade Security Section */}
      <section className="py-12 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(34, 197, 94, 0.05) 100%)',
      }}>
        {/* Animated gradient orbs */}
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, -30, 0],
            y: [0, 40, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-20 right-1/3 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, 40, 0],
            y: [0, -30, 0],
            scale: [1, 0.95, 1],
          }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              Your Data is Protected by an Enterprise-Grade Security Foundation
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              Security is not an add-on; it's architected into the core of our platform. We ensure the integrity and confidentiality of your most sensitive information.
            </motion.p>
          </motion.div>

          <SecurityCardsCarousel
            cards={[
              <div key="card-1" className="w-72 sm:w-80 h-72 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 border border-slate-700 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-105 text-center flex flex-col items-center justify-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-cyan-500 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                  <User size={24} className="text-white sm:hidden" />
                  <User size={28} className="text-white hidden sm:block" />
                </div>
                <h3 className="text-white font-bold mb-2 sm:mb-3 text-base sm:text-lg">Built-in User Management</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed">Granular control ensures only authorized users can access specific documents and workflows.</p>
              </div>,
              <div key="card-2" className="w-72 sm:w-80 h-72 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 border border-slate-700 shadow-lg shadow-purple-500/20 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/50 hover:scale-105 text-center flex flex-col items-center justify-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-500 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                  <CheckSquare size={24} className="text-white sm:hidden" />
                  <CheckSquare size={28} className="text-white hidden sm:block" />
                </div>
                <h3 className="text-white font-bold mb-2 sm:mb-3 text-base sm:text-lg">Data Integrity</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed">Automated workflows reduce the risk of unauthorized changes to critical information.</p>
              </div>,
              <div key="card-3" className="w-72 sm:w-80 h-72 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 border border-slate-700 shadow-lg shadow-green-500/20 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/50 hover:scale-105 text-center flex flex-col items-center justify-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-green-500 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                  <ShieldCheck size={24} className="text-white sm:hidden" />
                  <ShieldCheck size={28} className="text-white hidden sm:block" />
                </div>
                <h3 className="text-white font-bold mb-2 sm:mb-3 text-base sm:text-lg">Quantum-Safe with ML-KEM</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed">Post-Quantum Cryptography (PQC) using ML-KEM algorithm to protect against quantum threats.</p>
              </div>,
              <div key="card-4" className="w-72 sm:w-80 h-72 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 border border-slate-700 shadow-lg shadow-blue-500/20 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/50 hover:scale-105 text-center flex flex-col items-center justify-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-500 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                  <Lock size={24} className="text-white sm:hidden" />
                  <Lock size={28} className="text-white hidden sm:block" />
                </div>
                <h3 className="text-white font-bold mb-2 sm:mb-3 text-base sm:text-lg">Complete Encryption with Zero Trust</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed">All data fully encrypted with Zero Trust access controls for verification and security.</p>
              </div>,
              <div key="card-5" className="w-72 sm:w-80 h-72 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 border border-slate-700 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-105 text-center flex flex-col items-center justify-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-cyan-500 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                  <ScrollText size={24} className="text-white sm:hidden" />
                  <ScrollText size={28} className="text-white hidden sm:block" />
                </div>
                <h3 className="text-white font-bold mb-2 sm:mb-3 text-base sm:text-lg">Complete Audit Trails</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed">Every action is logged for full accountability and transparency.</p>
              </div>,
              <div key="card-6" className="w-72 sm:w-80 h-72 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 sm:p-8 border border-slate-700 shadow-lg shadow-emerald-500/20 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/50 hover:scale-105 text-center flex flex-col items-center justify-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-emerald-500 rounded-lg flex items-center justify-center mb-4 sm:mb-5">
                  <Brain size={24} className="text-white sm:hidden" />
                  <Brain size={28} className="text-white hidden sm:block" />
                </div>
                <h3 className="text-white font-bold mb-2 sm:mb-3 text-base sm:text-lg">Ethical AI</h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed">Our AI is engineered to eliminate hallucinations and deliver unbiased, transparent decision-making aligned with your enterprise values.</p>
              </div>
            ]}
            speed={0.8}
          />
        </div>
      </section>

      {/* Why Ordinaa */}
      <section className="py-12 relative overflow-hidden z-10" style={{
        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.06) 0%, rgba(59, 130, 246, 0.04) 100%)',
      }}>
        {/* Animated gradient elements */}
        <motion.div
          className="absolute top-1/4 left-0 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, 30, 0],
            scale: [1, 1.05, 1],
          }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-80 h-80 rounded-full blur-3xl opacity-15"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
          }}
          animate={{
            x: [0, -40, 0],
            y: [0, 30, 0],
            scale: [1, 0.95, 1],
          }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Industry Leaders Choose Ordinaa</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We combine deep domain expertise with state-of-the-art AI to deliver solutions that are not just innovative, but reliable, secure, and scalable for enterprise needs.
              </p>

              <div className="space-y-8">
                <motion.div
                  className="flex gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-100 text-cyan-600 rounded-xl flex items-center justify-center">
                    <Zap size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Built for Speed & Efficiency</h3>
                    <p className="text-gray-600">Our platforms are engineered to drastically reduce processing times, enabling your team to focus on high-value strategic tasks.</p>
                  </div>
                </motion.div>
                <motion.div
                  className="flex gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                    <BarChart size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Data-Driven Insights</h3>
                    <p className="text-gray-600">Gain real-time visibility into your operations with advanced analytics and customizable dashboards across all our products.</p>
                  </div>
                </motion.div>
                <motion.div
                  className="flex gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center">
                    <Shield size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Enterprise-Grade Security</h3>
                    <p className="text-gray-600">Security is woven into the fabric of every Ordinaa application with post quantum readiness.</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-300 to-blue-400 rounded-xl opacity-30 blur-xl"></div>
              <img
                src="/why choose us.webp"
                alt="Industry Leaders Choose Ordinaa"
                className="relative rounded-2xl shadow-2xl border border-gray-100 transform hover:scale-[1.02] transition-transform duration-500"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-br from-cyan-600 to-blue-700 text-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.h2
            className="text-3xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
          >
            Ready to Transform Your Operations?
          </motion.h2>
          <motion.p
            className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            Join the forward-thinking enterprises using Ordinaa to scale faster, hire smarter, and secure their future.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-blue-600 rounded-full font-bold text-lg hover:bg-cyan-50 transition-all shadow-lg hover:shadow-xl"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>

        {/* Striped reveal background animation */}
        <motion.div
          className="absolute inset-0 opacity-20"
          initial={{ backgroundPosition: "200% 0" }}
          whileInView={{ backgroundPosition: "0% 0" }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          viewport={{ once: true }}
          style={{
            backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 35px, rgba(255,255,255,0.1) 35px, rgba(255,255,255,0.1) 70px)",
            backgroundPosition: "200% 0",
          }}
        />
      </section>
    </div>
  );
};

export default Home;
